import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import ProspectFiltersMultiSelectDropdown from '../../prospect-filters-multi-select-dropdown';

import { truncate } from '../../../../../../../shared/utils';
import {
  getProspectFilterFieldAttributeValues,
  staticDropdownKeysForProspectFilter,
} from '../../../utils/get-prospect-filters-field-attribute-values';
import { makeProspectFilterFieldAttributeAPICalls } from '../../../utils/make-prospect-filters-field-attribute-api-calls';
import {
  FilterOperation,
  ProspectFilterField,
  ProspectFiltersFieldsAttributesValue,
} from '../../../types';
import {
  ProspectFiltersSequencesClientsPayload,
  ProspectFiltersValuesPayload,
} from '../../../../../types';
import {
  getProspectFiltersMultiSelectSearchOptions,
  getSelectedUsers,
} from '../../../utils/helpers';
import TeamsFilter from '../../../../../../../shared/components/teams-filter';
import { TeamsAndUsersList } from '../../../../../../settings/types/users-and-teams';
import { isAgencyUser } from '../../../../../../../shared/utils/user-details';
import { renderPills } from '../utils/render-pills';
import ProspectFilterMultiLevelSelect from './prospect-filter-multi-level-select';
import { MultiLevelSelectOption } from '../utils/types';
import { getSelectedOptionKeys } from '../utils/get-selected-option-keys';
import { RootState } from '../../../../../../../store/root-reducer';

// Fields to not show creatable prospect filter value component
const DONT_SHOW_CREATABLE_PROSPECT_FILTER_VALUE_COMPONENT = [
  'status', // Prospect Status
  'outcomes', // Prospect Outcomes
  'verificationStatus', // Email Verification Status
  'emailServiceProvider', // Prospect ESP
  'owners', // Prospect Owner
  'creationDate', // Creation Date
  'lastUpdatedAt', // Last Updated
  'sequence', // Sequence
  'verificationSubStatus', // Email Verification Sub Status
  'freeEmailAccount', // Free Email Account
  'creationSource', // Creation Source
  'clientSequenceAssociation', // Client Associated with sequence
  'Prospect Overview', // Prospect Overview
  'subSequence', // Sub Sequence
];

type IProps = {
  value: (string | number)[];
  onUpdateFilterConditionValue: (value: (string | number)[]) => void;

  field: ProspectFilterField;
  operation: FilterOperation;
  disabled?: boolean;

  prospectFiltersFieldsAttributesValue: ProspectFiltersFieldsAttributesValue;
  prospectFiltersFieldsAttributesLoading: Record<string, boolean>;
  prospectFiltersFieldsAttributesFetched: Record<string, boolean>;
  sendGetProspectFiltersFieldValuesRequest: (
    payload: ProspectFiltersValuesPayload,
  ) => void;
  sendGetProspectFiltersAttributeFieldValuesRequest: (
    payload: ProspectFiltersValuesPayload,
  ) => void;
  sendGetProspectFiltersSequencesListRequest: (
    payload: ProspectFiltersSequencesClientsPayload,
  ) => void;
  sendGetProspectFiltersClientsListRequest: (
    payload: ProspectFiltersSequencesClientsPayload,
  ) => void;
  sendGetProspectFiltersSubsequenceListRequest: (
    payload: ProspectFiltersSequencesClientsPayload,
  ) => void;
  sendGetProspectFiltersTagsListRequest: VoidFunction;
  sendGetProspectFiltersOwnersListRequest: VoidFunction;
  sendGetProspectFiltersOwnersListForAgencyRequest: VoidFunction;
  sendGetProspectFiltersOutcomeListRequest: VoidFunction;
  sendGetProspectLeadFinderListsRequest: (payload: { key: string }) => void;

  resetProspectFiltersFieldsAttributesValue: (payload: { key: string }) => void;
  onApplyAiColEnrichmentAutoRunSettingsCondition?: any;
};

const ProspectFiltersSelectInput: React.FC<IProps> = ({
  value = [],
  onUpdateFilterConditionValue,

  field,
  operation,
  disabled = false,
  prospectFiltersFieldsAttributesValue,
  prospectFiltersFieldsAttributesLoading,
  prospectFiltersFieldsAttributesFetched,
  sendGetProspectFiltersFieldValuesRequest,
  sendGetProspectFiltersAttributeFieldValuesRequest,
  sendGetProspectFiltersSequencesListRequest,
  sendGetProspectFiltersClientsListRequest,
  sendGetProspectFiltersSubsequenceListRequest,
  sendGetProspectFiltersTagsListRequest,
  sendGetProspectFiltersOwnersListRequest,
  sendGetProspectFiltersOwnersListForAgencyRequest,
  sendGetProspectFiltersOutcomeListRequest,
  sendGetProspectLeadFinderListsRequest = () => {},
  resetProspectFiltersFieldsAttributesValue,
  onApplyAiColEnrichmentAutoRunSettingsCondition,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);
  const [resetSelectedUserIds, setResetSelectedUserIds] = useState(false);
  const operationValueRef = useRef(null);
  const isSearchDirty = useRef(false);
  const previousFieldKeyRef = useRef(field?.key);

  // Reset values when field changes
  useEffect(() => {
    if (previousFieldKeyRef.current !== field?.key) {
      setSearchTerm('');
      setSelectedValues([]);
      isSearchDirty.current = false;
      previousFieldKeyRef.current = field?.key;
    }
  }, [field?.key]);

  useMemo(() => {
    if (operationValueRef?.current !== operation) {
      setResetSelectedUserIds(true);
      operationValueRef.current = operation;
    }
  }, [operation]);

  const featureUsages = useSelector(
    (state: RootState) => state?.home?.featureUsages,
  );
  const isProspectEnrollmentEnabled = useSelector(
    (state: RootState) => state?.home?.isProspectEnrollmentEnabled,
  );
  const {
    key,
    meta,
    shouldFetch,
    sendValuesInPayload,
    options,
  } = getProspectFilterFieldAttributeValues(
    field,
    value,
    selectedValues,
    prospectFiltersFieldsAttributesValue,
    featureUsages,
    isProspectEnrollmentEnabled,
  );

  const {
    showSearch,
    useFilterOption,
    useAPICall,
  } = getProspectFiltersMultiSelectSearchOptions(field);

  const flattenedTeamsUsers = useMemo(() => {
    if (field.key === 'owners' && !isAgencyUser()) {
      // Flatten the users from all teams
      const users = options.flatMap((team) => team.users);

      // Use a Set to get unique users by their id
      const uniqueUsers = Array.from(
        new Set(users.map((user) => user.id)),
      ).map((id) => users.find((user) => user.id === id));

      return uniqueUsers; // Return array of unique users
    }

    return [];
  }, [field, options]);

  const makeApiCall = (payload = null) => {
    makeProspectFilterFieldAttributeAPICalls({
      field,
      fieldData: options,
      fetchField: shouldFetch,
      value,
      sendValuesInPayload,
      fieldsLoading: prospectFiltersFieldsAttributesLoading,
      fieldsFetched: prospectFiltersFieldsAttributesFetched,
      meta: payload,
      sendGetProspectLeadFinderListsRequest,
      sendGetProspectFiltersFieldValuesRequest,
      sendGetProspectFiltersAttributeFieldValuesRequest,
      sendGetProspectFiltersSequencesListRequest,
      sendGetProspectFiltersClientsListRequest,
      sendGetProspectFiltersSubsequenceListRequest,
      sendGetProspectFiltersTagsListRequest,
      sendGetProspectFiltersOwnersListRequest,
      sendGetProspectFiltersOwnersListForAgencyRequest,
      sendGetProspectFiltersOutcomeListRequest,
    });
  };

  // Define debounce function
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // Debounced API call function
  const handleSearch = useCallback(
    debounce((search) => {
      makeApiCall({
        page: 1,
        pageSize: meta?.pageSize,
        search,
      });
    }, 400),
    [meta],
  );

  useEffect(() => {
    if (isSearchDirty.current) {
      handleSearch(searchTerm);
      if (searchTerm === '') {
        isSearchDirty.current = false;
      }
    }
  }, [searchTerm]);

  useMemo(() => {
    if (!staticDropdownKeysForProspectFilter.includes(field.key)) {
      makeApiCall();
    }
  }, [field.key, shouldFetch]);

  const isLoading =
    options.length === 0 &&
    (prospectFiltersFieldsAttributesLoading?.[field.key] ||
      prospectFiltersFieldsAttributesLoading?.[field.id]);

  const getPlaceholderText = () => {
    if (field?.key === 'clientSequenceAssociation') {
      return 'Select Clients';
    }
    return `Select ${field?.value || 'Value'}`;
  };

  if (field.key === 'verificationSubStatus' || field.key === 'subSequence') {
    return (
      <ProspectFilterMultiLevelSelect
        value={value as string[]}
        options={options as MultiLevelSelectOption[]}
        onChange={(userIds) => {
          onUpdateFilterConditionValue(userIds);
        }}
        placeholder={getPlaceholderText()}
        disabled={disabled}
      />
    );
  }

  if (field.key === 'owners' && !isAgencyUser()) {
    return (
      <TeamsFilter
        selectLabel="owners"
        btnClassName="btn-white w-1 prospect-filters-owners-select"
        defaultSelectedUsers={value as number[]}
        resetSelectedUserIds={resetSelectedUserIds}
        setResetSelectedUserIds={setResetSelectedUserIds}
        onUserIdsChange={(userIds) => {
          onUpdateFilterConditionValue(userIds);
        }}
        shouldMakeInitialAPICall={false}
        data={(options as unknown) as TeamsAndUsersList[]}
        customSelectBox={
          <>
            {!value || value?.length === 0 ? (
              <span className="placeholder">Select Prospect Owner</span>
            ) : (
              renderPills(
                getSelectedUsers(value as number[], flattenedTeamsUsers),
                onUpdateFilterConditionValue,
                disabled,
              )
            )}
          </>
        }
        hideFooter={true}
        transferClassName="prospect-filters-owners-dropdown"
        disabled={disabled}
      />
    );
  }

  // Use CreatableSelect when onApplyAiColEnrichmentAutoRunSettingsCondition prop is present
  // and operation is IS or IS_NOT
  // but NOT for fields in DONT_SHOW_CREATABLE_PROSPECT_FILTER_VALUE_COMPONENT
  if (
    onApplyAiColEnrichmentAutoRunSettingsCondition &&
    (operation === FilterOperation.IS ||
      operation === FilterOperation.IS_NOT) &&
    !DONT_SHOW_CREATABLE_PROSPECT_FILTER_VALUE_COMPONENT.includes(field.key)
  ) {
    // Convert options to react-select format

    const reactSelectOptions = options.map((option) => ({
      value: option.key || option.value,
      label: option.name || option.value || option.key,
    }));

    // Convert selected values to react-select format
    const selectedReactSelectValues = value
      .map((val) => {
        const option = options.find(
          (opt) => opt.key === val || opt.value === val,
        );
        if (option) {
          return {
            value: option.key || option.value,
            label: option.name || option.value || option.key,
          };
        }
        // If no match found in options, it's a newly created value
        return {
          value: val,
          label: val,
        };
      })
      .filter(Boolean);

    return (
      <div className="conditions-creatable-select-box">
        <CreatableSelect
          isMulti
          options={reactSelectOptions}
          value={selectedReactSelectValues}
          onChange={(selectedOptions) => {
            const values = selectedOptions
              ? selectedOptions.map((option) => option.value)
              : [];
            onUpdateFilterConditionValue(values);
          }}
          placeholder={getPlaceholderText()}
          isDisabled={disabled}
          isLoading={isLoading}
          className="react-select-container"
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>
    );
  }

  return (
    <ProspectFiltersMultiSelectDropdown
      className="fields-select-box"
      selectClassName="fields-dropdown"
      placeholder={getPlaceholderText()}
      options={options}
      isLoading={isLoading}
      disabled={disabled}
      optionRenderer={(option) => (
        <span>{truncate(option?.name || '', 42, false)}</span>
      )}
      selectedOptionKey={getSelectedOptionKeys({
        value: value as string[],
        field,
      })}
      selectedOptionRenderer={(selectedOptions) =>
        renderPills(selectedOptions, onUpdateFilterConditionValue, disabled)
      }
      onChange={(selectedOptions) => {
        setSelectedValues(selectedOptions);

        onUpdateFilterConditionValue(
          selectedOptions.flatMap((item) => {
            if (item?.value && Array.isArray(item?.value)) {
              return item.value;
            }
            return item.key;
          }),
        );
      }}
      shouldShowLoadMoreBtn={meta?.hasMoreData}
      isLoadingMore={
        prospectFiltersFieldsAttributesLoading?.[field.key] ||
        prospectFiltersFieldsAttributesLoading?.[field.id]
      }
      onLoadMore={() => {
        makeApiCall({
          page: meta?.page + 1,
          pageSize: meta?.pageSize,
          search: meta?.search,
        });
      }}
      showSearch={showSearch}
      {...(useFilterOption && {
        filterOption: (search, option) =>
          option?.value?.toLowerCase().includes(search.toLowerCase()) ||
          option?.name?.toLowerCase().includes(search.toLowerCase()),
      })}
      {...(useAPICall && {
        resetProspectFiltersFieldsAttributesValue: () => {
          resetProspectFiltersFieldsAttributesValue({ key });
          if (isSearchDirty.current) {
            setSearchTerm('');
          }
        },
        onSearch: (search) => {
          setSearchTerm(search);
          if (!isSearchDirty.current) {
            isSearchDirty.current = true;
          }
        },
      })}
    />
  );
};

export default ProspectFiltersSelectInput;
